import { createDefaultIfElseConfig } from '../components/automations/IfElseConditionEditor';
import { createStepsFromCatalogAction } from './workflowStepGrouping';

/**
 * Create a workflow step from a catalog action — mirrors PlatformWorkflowBuilderPage.handleSelectAction.
 */
export function createStepFromCatalogAction(action, { timestamp = Date.now() } = {}) {
  const isEmailAction = action.id === 'email_action' || action.id === 'send_email';
  const isConditionAction = action.id === 'condition' || action.id === 'if_else';

  return {
    id: `step_${timestamp}`,
    type: isConditionAction ? 'if_else' : (action.stepType || action.id),
    label: isEmailAction ? 'Send Email' : isConditionAction ? 'Condition' : action.name,
    name: isEmailAction ? 'Send Email' : isConditionAction ? 'Condition' : action.name,
    config: isEmailAction
      ? {
          actionName: 'Send Email',
          name: 'Send Email',
          from_name: 'FundElite',
          fromName: 'FundElite',
          to: '{{contact.email}}',
          audience: 'client',
        }
      : isConditionAction
        ? createDefaultIfElseConfig()
        : {},
  };
}

/**
 * Insert a catalog action at the workflow step list index — same splice rules as the real builder.
 */
export function insertCatalogActionAtIndex(steps, insertAtIndex, action, options = {}) {
  const timestamp = options.timestamp ?? Date.now();
  const groupedSteps = createStepsFromCatalogAction(action, timestamp);

  if (groupedSteps?.length) {
    const newSteps = [...steps];
    const insertAt = insertAtIndex !== null && insertAtIndex >= 0
      ? insertAtIndex + 1
      : newSteps.length;
    newSteps.splice(insertAt, 0, ...groupedSteps);
    return {
      newSteps,
      insertedSteps: groupedSteps,
      primaryStep: groupedSteps[0],
      isGroup: true,
    };
  }

  const newStep = createStepFromCatalogAction(action, { timestamp });
  const newSteps = [...steps];
  if (insertAtIndex !== null && insertAtIndex >= 0) {
    newSteps.splice(insertAtIndex + 1, 0, newStep);
  } else {
    newSteps.push(newStep);
  }

  return {
    newSteps,
    insertedSteps: [newStep],
    primaryStep: newStep,
    isGroup: false,
  };
}

/** Map a step type to editor panel flags used by useWorkflowEditorLayout. */
export function resolveEditorFlagsForStep(step) {
  if (!step) {
    return {};
  }

  const type = String(step.type || '').toLowerCase();

  return {
    showEmailEditor: type === 'send_email' || type === 'email_action',
    showSMSEditor: type === 'send_sms' || type === 'sms' || type === 'sms_action',
    showWaitEditor: type === 'wait',
    showConditionEditor: type === 'condition' || type === 'if_else',
    showNotificationEditor: type === 'internal_notification' || type === 'send_notification',
    showBackendActionEditor: type === 'backend_action' || Boolean(step.config?.requires_backend),
  };
}

export function buildProofEditorLayoutFlags({
  showActionsPanel = false,
  editingStep = null,
  editorVisible = false,
} = {}) {
  const base = {
    variant: 'platform',
    showActionsPanel,
    showTriggerPicker: false,
    showRunTestPanel: false,
    runTestExpanded: false,
  };

  if (!editorVisible || !editingStep) {
    return base;
  }

  return {
    ...base,
    ...resolveEditorFlagsForStep(editingStep),
  };
}
